const _excluded = ["children", "projectId", "accountId", "locale", "autoFetchEntities", "currentProductId", "currentToolId", "variant", "isProductRequired", "redirectToProductIds", "onProductClick", "onAccountClick", "onProjectClick"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import { fetchWithTokenAndRegion, getAccHostname, getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { identify, setBaseProperties, waitForBaseProperties } from '@adsk/acs-web-analytics';
import { clearNetworkCache } from '@adsk/alloy-helpers';
import { AlloyProvider, useAlloyFeatureFlag, useCurrentProject, useUser, useCurrentAccount, useCurrentProductId, useCurrentProjectId, PRODUCT_IDS, SERVICE_TYPES, TOOL_IDS, useCurrentAccountId } from '@adsk/alloy-provider';
import { warn } from '@adsk/alloy-react-helpers';
import { PRODUCTS, PAGE_VARIANTS, ADMIN_PRODUCT_CATEGORY, POST_LICENSE_CONFIG } from './consts';
import appendQuerySymbol from './helpers/appendQuerySymbol';
import getAccountIdFromUrl from './helpers/getAccountIdFromUrl';
import getCurrentAccountProductId from './helpers/getCurrentAccountProductId';
import getCurrentProductId from './helpers/getCurrentProductId';
import getCurrentToolId from './helpers/getCurrentToolId';
import { getMFABlockPageUrl } from './helpers/getMFABlockPageUrl';
import getProjectIdFromUrl from './helpers/getProjectIdFromUrl';
import getRedirectUrlToAvailableProduct from './helpers/getRedirectUrlToAvailableProduct';
import navigate from './helpers/navigate';
import ProjectPageLayoutView from './ProjectPageLayoutView';
import { RefreshTokenSetter } from './RefreshTokenSetter';
import { useProductsAccessTypes } from './useProductsAccessTypes';
const getProjectListUrl = () => {
  return "".concat(getAccHostname(), "/projects");
};
const getPageNotFoundUrl = () => {
  return "".concat(getAccHostname(), "/page-not-found");
};
const CheckRedirectToProjectsListPageForUnavailableAccountProduct = () => {
  const currentAccount = useCurrentAccount();
  const currentProductId = useCurrentProductId();
  const currentProduct = PRODUCTS.find(p => p.id === currentProductId);
  const productServiceType = currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.serviceType;
  useEffect(() => {
    if (productServiceType && currentAccount && currentAccount.services) {
      const projectServiceType = currentAccount.services.find(s => s.service_type === productServiceType);
      if (!projectServiceType) {
        console.error('ProjectPageLayout - redirecting to project list for unauthorized product in account');
        clearNetworkCache();
        navigate(getProjectListUrl());
      }
    }
  }, [productServiceType, currentAccount]);
  return null;
};
const CheckRedirectToProjectsListPageForUnavailableProjectProduct = _ref => {
  let {
    currentToolId,
    redirectToProductIds
  } = _ref;
  const currentProject = useCurrentProject();
  const currentProductId = useCurrentProductId();
  const isTriggeredRedirect = useRef(false);
  useEffect(() => {
    const {
      pathname
    } = window.location;
    if (currentProductId && currentProject && currentProject.products && !isTriggeredRedirect.current) {
      const currentProduct = currentProject === null || currentProject === void 0 ? void 0 : currentProject.products.find(p => p.key === currentProductId);
      if (!currentProduct) {
        const otherProductUrl = getRedirectUrlToAvailableProduct({
          currentProject,
          redirectToProductIds,
          currentToolId
        });
        console.error(otherProductUrl ? "ProjectPageLayout - redirecting to other product in project, ".concat(otherProductUrl) : 'ProjectPageLayout - redirecting to project list for unauthorized product in project');
        clearNetworkCache();
        navigate(otherProductUrl || getProjectListUrl());
        isTriggeredRedirect.current = true;
      } else if (!currentProduct.authorized && !pathname.startsWith('/access')) {
        console.error('ProjectPageLayout - redirecting unauthorized product');
        clearNetworkCache();
        const {
          href
        } = window.location;
        const baseUrl = currentProduct.defaultUrl;
        const queryParam = "redirectUrl=".concat(encodeURIComponent(href));
        let formattedUrl = appendQuerySymbol(baseUrl, queryParam);
        if (!formattedUrl) {
          formattedUrl = baseUrl;
        }
        const redirectUrl = formattedUrl;
        navigate(redirectUrl);
        isTriggeredRedirect.current = true;
      }
    }
  }, [currentProductId, currentToolId, currentProject, redirectToProductIds]);
  return null;
};
CheckRedirectToProjectsListPageForUnavailableProjectProduct.propTypes = {
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  redirectToProductIds: PropTypes.arrayOf(PropTypes.oneOf(Object.values(PRODUCT_IDS)))
};
const CheckRedirectToProjectsListOrPageNotFoundPageForUnavailableProjectTool = _ref2 => {
  let {
    currentToolId,
    redirectToProductIds,
    slashClientsFF,
    slashClientsFFLoading
  } = _ref2;
  const currentProject = useCurrentProject();
  const currentProductId = useCurrentProductId();
  useEffect(() => {
    if (currentProductId && currentToolId && currentProject && currentProject.products && !slashClientsFFLoading) {
      const byKey = key => o => o.key === key;
      const currentProduct = currentProject === null || currentProject === void 0 ? void 0 : currentProject.products.find(byKey(currentProductId));

      // If product is missing we handle it in product specific redirect
      if (currentProduct) {
        const currentTool = currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.tools.find(byKey(currentToolId));
        if (!currentTool) {
          const otherProductUrl = getRedirectUrlToAvailableProduct({
            currentProject,
            redirectToProductIds,
            currentToolId
          });
          console.error(otherProductUrl ? "ProjectPageLayout - redirecting to other tool in project, ".concat(otherProductUrl) : slashClientsFF ? 'ProjectPageLayout - redirecting to page not found for unauthorized tool in project' : 'ProjectPageLayout - redirecting to project list for unauthorized tool in project');
          clearNetworkCache();
          navigate(otherProductUrl || (slashClientsFF ? getPageNotFoundUrl() : getProjectListUrl()));
        }
      }
    }
  }, [currentToolId, currentProductId, currentProject, redirectToProductIds, slashClientsFFLoading, slashClientsFF]);
  return null;
};
CheckRedirectToProjectsListOrPageNotFoundPageForUnavailableProjectTool.propTypes = {
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  redirectToProductIds: PropTypes.arrayOf(PropTypes.oneOf(Object.values(PRODUCT_IDS))),
  slashClientsFFLoading: PropTypes.bool,
  slashClientsFF: PropTypes.bool
};
const CheckRedirectToProjectsListPageForUnavailableProduct = _ref3 => {
  let {
    currentToolId,
    redirectToProductIds,
    variant
  } = _ref3;
  return variant === PAGE_VARIANTS.ACCOUNT ? /*#__PURE__*/React.createElement(CheckRedirectToProjectsListPageForUnavailableAccountProduct, null) : /*#__PURE__*/React.createElement(CheckRedirectToProjectsListPageForUnavailableProjectProduct, {
    currentToolId: currentToolId,
    redirectToProductIds: redirectToProductIds
  });
};
CheckRedirectToProjectsListPageForUnavailableProduct.propTypes = {
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  redirectToProductIds: PropTypes.arrayOf(PropTypes.oneOf(Object.values(PRODUCT_IDS))),
  variant: PropTypes.oneOf(Object.values(PAGE_VARIANTS))
};
const CheckRedirectIfAccountIdDoesntMatchCurrentProject = _ref4 => {
  let {
    accountId
  } = _ref4;
  const currentProject = useCurrentProject();
  const accountIdFromCurrentProject = currentProject === null || currentProject === void 0 ? void 0 : currentProject.accountId;
  useEffect(() => {
    if (accountId && accountIdFromCurrentProject && accountIdFromCurrentProject !== accountId) {
      console.error('ProjectPageLayout - redirecting to project list for account ID and project ID misalignment');
      clearNetworkCache();
      navigate(getProjectListUrl());
    }
  }, [accountId, accountIdFromCurrentProject]);
  return null;
};
CheckRedirectIfAccountIdDoesntMatchCurrentProject.propTypes = {
  accountId: PropTypes.string
};
const CheckRedirectToMFASetup = _ref5 => {
  let {
    isMFASetupRequiredFFLoading
  } = _ref5;
  const currentProjectId = useCurrentProjectId();
  const currentAccount = useCurrentAccount();
  useEffect(() => {
    if (!isMFASetupRequiredFFLoading && currentAccount !== null && currentAccount !== void 0 && currentAccount.mfaBlocked) {
      console.error('ProjectPageLayout - redirecting to multi factor authentication block page for unauthorized account');
      clearNetworkCache();
      navigate(getMFABlockPageUrl({
        accountId: currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.account_id,
        projectId: currentProjectId,
        redirectUrl: window.location.href
      }));
    }
  }, [currentAccount, currentProjectId, isMFASetupRequiredFFLoading]);
  return null;
};
CheckRedirectToMFASetup.propTypes = {
  isMFASetupRequiredFFLoading: PropTypes.bool
};
const CheckRedirectToProjectsListForArchivedProject = () => {
  const currentProject = useCurrentProject();
  const currentProjectStatus = currentProject === null || currentProject === void 0 ? void 0 : currentProject.status;
  useEffect(() => {
    if (currentProjectStatus && currentProjectStatus === 'archived') {
      console.error('ProjectPageLayout - redirecting to project list for project ID due to project archival');
      navigate(getProjectListUrl());
    }
  }, [currentProjectStatus]);
  return null;
};
const CheckRedirects = _ref6 => {
  let {
    accountId,
    currentToolId,
    redirectToProductIds,
    variant
  } = _ref6;
  const {
    value: slashClientsFF,
    loading: slashClientsFFLoading
  } = useAlloyFeatureFlag('hide-product-and-tools-reference-picker', false);
  const {
    value: isMFASetupRequiredFF,
    loading: isMFASetupRequiredFFLoading
  } = useAlloyFeatureFlag('mfa-setup-required', false);
  return /*#__PURE__*/React.createElement(React.Fragment, null, variant === PAGE_VARIANTS.ACCOUNT && /*#__PURE__*/React.createElement(CheckRedirectToProjectsListPageForUnavailableAccountProduct, null), /*#__PURE__*/React.createElement(CheckRedirectToProjectsListPageForUnavailableProduct, {
    currentToolId: currentToolId,
    redirectToProductIds: redirectToProductIds,
    variant: variant
  }), /*#__PURE__*/React.createElement(CheckRedirectToProjectsListOrPageNotFoundPageForUnavailableProjectTool, {
    currentToolId: currentToolId,
    redirectToProductIds: redirectToProductIds,
    slashClientsFFLoading: slashClientsFFLoading,
    slashClientsFF: slashClientsFF
  }), /*#__PURE__*/React.createElement(CheckRedirectToProjectsListForArchivedProject, null), /*#__PURE__*/React.createElement(CheckRedirectIfAccountIdDoesntMatchCurrentProject, {
    accountId: accountId
  }), isMFASetupRequiredFF && /*#__PURE__*/React.createElement(CheckRedirectToMFASetup, {
    isMFASetupRequiredFFLoading: isMFASetupRequiredFFLoading
  }));
};
CheckRedirects.propTypes = {
  accountId: PropTypes.string,
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  redirectToProductIds: PropTypes.arrayOf(PropTypes.oneOf(Object.values(PRODUCT_IDS))),
  variant: PropTypes.oneOf(Object.values(PAGE_VARIANTS))
};
const AnalyticsInitializer = _ref7 => {
  let {
    toolIdToUse,
    isToolIdDetermined,
    isProductRequired,
    productIdToUse
  } = _ref7;
  const currentAccountId = useCurrentAccountId();
  const currentProject = useCurrentProject();
  const currentProjectId = useCurrentProjectId();
  const {
    user
  } = useUser();
  const {
    data: productsAccessTypesData,
    loading: productsAccessTypesLoading
  } = useProductsAccessTypes();
  const notApplicable = 'N/A';
  useLayoutEffect(() => {
    if (isProductRequired) {
      waitForBaseProperties('product_id', 'license');
    } else {
      waitForBaseProperties('license');
    }
  }, [isProductRequired]);
  useEffect(() => {
    !productsAccessTypesLoading && user && identify(user.analyticsId, _objectSpread(_objectSpread({}, user), {}, {
      licenses: productsAccessTypesData
    }));
  }, [productsAccessTypesData, productsAccessTypesLoading, user]);
  useEffect(() => {
    if (!user || productsAccessTypesLoading) {
      return;
    }
    if (!isProductRequired) {
      // license is found by product ID which we don't have
      setBaseProperties({
        license: notApplicable
      });
    }
    if (productIdToUse) {
      const licenses = productsAccessTypesData;
      const license = licenses === null || licenses === void 0 ? void 0 : licenses.find(l => l.productType === productIdToUse);
      setBaseProperties({
        license: license ? license.type : 'none'
      });
    }
  }, [user, isProductRequired, productIdToUse, productsAccessTypesData, productsAccessTypesLoading]);
  useEffect(() => {
    setBaseProperties({
      product_id: productIdToUse || notApplicable
    });
  }, [productIdToUse]);
  useEffect(() => {
    setBaseProperties({
      tool_id: isToolIdDetermined && toolIdToUse ? toolIdToUse : notApplicable
    });
  }, [isToolIdDetermined, toolIdToUse]);
  useEffect(() => {
    setBaseProperties({
      project_id: currentProjectId || notApplicable
    });
  }, [currentProjectId]);
  useEffect(() => {
    var _currentProject$acces;
    setBaseProperties({
      project_classification: (currentProject === null || currentProject === void 0 ? void 0 : currentProject.classification) || notApplicable,
      is_project_admin: (currentProject === null || currentProject === void 0 ? void 0 : (_currentProject$acces = currentProject.accessLevels) === null || _currentProject$acces === void 0 ? void 0 : _currentProject$acces.projectAdministrator) || notApplicable
    });
  }, [currentProject]);
  useEffect(() => {
    if (currentAccountId) {
      setBaseProperties({
        account_id: currentAccountId
      });
    }
  }, [currentAccountId]);
  return null;
};
AnalyticsInitializer.propTypes = {
  toolIdToUse: PropTypes.oneOf(Object.values(TOOL_IDS)),
  isToolIdDetermined: PropTypes.bool,
  isProductRequired: PropTypes.bool,
  productIdToUse: PropTypes.oneOf(Object.values(PRODUCT_IDS))
};
const CurrentProductSetter = _ref8 => {
  let {
    setProductId,
    variant,
    redirectToProductIds,
    currentToolId
  } = _ref8;
  const currentProject = useCurrentProject();
  const currentAccount = useCurrentAccount();
  useEffect(() => {
    if (variant === PAGE_VARIANTS.PROJECT && currentProject) {
      const productId = getCurrentProductId(currentProject);
      if (productId) {
        setProductId(productId);
      } else {
        const otherProductUrl = getRedirectUrlToAvailableProduct({
          currentProject,
          redirectToProductIds,
          currentToolId
        });
        console.error(otherProductUrl ? "ProjectPageLayout - redirecting to other available product in project, ".concat(otherProductUrl) : 'ProjectPageLayout - redirecting to project list for unauthorized access to product in project');
        clearNetworkCache();
        navigate(otherProductUrl || getProjectListUrl());
      }
    }
  }, [currentProject, setProductId, variant, currentToolId, redirectToProductIds]);
  useEffect(() => {
    if (variant === PAGE_VARIANTS.ACCOUNT && currentAccount) {
      const productId = getCurrentAccountProductId(currentAccount);
      if (productId) {
        setProductId(productId);
      } else {
        console.error('ProjectPageLayout - redirecting to project list for unauthorized access to product in account');
        clearNetworkCache();
        navigate(getProjectListUrl());
      }
    }
  }, [currentAccount, setProductId, variant]);
  return null;
};
CurrentProductSetter.propTypes = {
  setProductId: PropTypes.func,
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  redirectToProductIds: PropTypes.arrayOf(PropTypes.oneOf(Object.values(PRODUCT_IDS))),
  variant: PropTypes.oneOf(Object.values(PAGE_VARIANTS))
};
const CurrentToolSetter = _ref9 => {
  let {
    setToolId,
    setIsToolIdDetermined
  } = _ref9;
  const currentProject = useCurrentProject();
  const currentProductId = useCurrentProductId();
  useEffect(() => {
    if (currentProject && currentProductId) {
      const toolId = getCurrentToolId(currentProject, currentProductId);
      if (toolId) {
        setToolId(toolId);
      }
      setIsToolIdDetermined(true);
    }
  }, [currentProject, currentProductId, setToolId, setIsToolIdDetermined]);
  return null;
};
CurrentToolSetter.propTypes = {
  setToolId: PropTypes.func,
  setIsToolIdDetermined: PropTypes.func
};
const CreateAndCheckCurrentProductLicense = _ref10 => {
  let {
    currentToolId,
    isProductRequired,
    variant
  } = _ref10;
  const currentProject = useCurrentProject();
  const currentProjectId = useCurrentProjectId();
  const currentProductId = useCurrentProductId();
  const createProductLicense = useCallback(() => fetchWithTokenAndRegion("".concat(getAdminApiUrl(), "/v1/licenses"), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      projectId: currentProjectId,
      productKey: currentProductId
    })
  }), [currentProjectId, currentProductId]);
  const shouldCreateLicense = useMemo(() => {
    const {
      pathname
    } = window.location;
    const isAccessPage = pathname.startsWith('/access');
    const currentProduct = currentProject === null || currentProject === void 0 ? void 0 : currentProject.products.find(product => product.key === currentProductId);
    const isAdminProduct = (currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.category) === ADMIN_PRODUCT_CATEGORY;
    return variant === PAGE_VARIANTS.PROJECT && isProductRequired && !isAccessPage && !!currentProduct && !isAdminProduct;
  }, [currentProductId, currentProject, isProductRequired, variant]);
  const {
    data,
    error,
    mutate
  } = useSWR(shouldCreateLicense ? "license_".concat(currentProductId, "_").concat(currentProjectId) : null, createProductLicense, POST_LICENSE_CONFIG);
  useEffect(() => {
    if (!error) return;
    console.error('ProjectPageLayout - redirecting to access open for create license error', error);
    const {
      href
    } = window.location;
    const ACCESS_OPEN = "".concat(getAccHostname(), "/access/open");
    const redirectUrl = "".concat(ACCESS_OPEN, "/projects/").concat(currentProjectId, "?pk=").concat(currentProductId, "&redirectUrl=").concat(encodeURIComponent(href));
    navigate(redirectUrl);
  }, [currentProductId, currentProjectId, error]);
  useEffect(() => {
    if (!data) return;
    const {
      license,
      redirectUrl
    } = data;
    if (!license.authorized) {
      console.error('ProjectPageLayout - redirecting unauthorized license');
      navigate(redirectUrl);
    }
    const isLicenseExpired = new Date(license.reauthorize) < new Date(Date.now());
    if (isLicenseExpired) {
      mutate();
    }
  }, [currentToolId, data, mutate]);
  return null;
};
CreateAndCheckCurrentProductLicense.propTypes = {
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  isProductRequired: PropTypes.bool,
  variant: PropTypes.string
};

/**
 * ProjectPageLayout component is the root component for specific project or account page views.
 * This component needs to be as up as we can in React components tree, and handles 3 things mainly:
 *
 * 1. Page layout - rendering top & side navigation components, including projects picker, product picker and more, all according to user's entitlements.
 * 2. Data providers - adds AlloyProvider, which fetches relevant data and allows all children to access the data using hooks (useUser, useProjectEntitlements, etc.)
 * 3. Redirects - redirect the user to pages they can see (usually projects list page):
 *       * User is not logged in - redirects to login page.
 *       * User tries to access project page without any available projects.
 *       * User tries to access a product they have no entitlements to.
 *       * User tries to access a tool they have no entitlements to.
 *       * User tries to access project page without project ID in the URL (or as a prop).
 *       * User tries to access account page without account ID in the URL (or as a prop).
 *       * When using both project ID and account ID, but the requested project doesn't exist in that account.
 *       * When entitlements response contains `valid_service_location` the user needs to go to.
 *       * If `redirectToProductIds` and `currentToolId` exists, user would get redirected to same URL in a different product if possible.
 *         For example opening Issues tool in Build when user can only access Docs would redirect the user to Issues in Docs.
 */
const ProjectPageLayout = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      children,
      projectId,
      accountId,
      locale,
      autoFetchEntities,
      currentProductId: productIdFromProp,
      currentToolId: toolIdFromProp,
      variant,
      isProductRequired,
      redirectToProductIds,
      onProductClick,
      onAccountClick,
      onProjectClick
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const [productIdFromState, setProductFromIdState] = useState();
  const [toolIdFromState, setToolFromIdState] = useState();
  const [isToolIdDetermined, setIsToolIdDetermined] = useState(!!toolIdFromProp);
  const projectIdToUse = projectId || getProjectIdFromUrl();
  const accountIdToUse = accountId || getAccountIdFromUrl();
  const productIdToUse = productIdFromProp || productIdFromState;
  const toolIdToUse = toolIdFromProp || toolIdFromState;
  useEffect(() => {
    if (variant === PAGE_VARIANTS.PROJECT && !projectIdToUse) {
      console.error('ProjectPageLayout - redirecting to projects list. Missing project ID in URL');
      navigate(getProjectListUrl());
    }
  }, [variant, projectIdToUse]);
  useEffect(() => {
    if (variant === PAGE_VARIANTS.ACCOUNT && !accountIdToUse) {
      console.error('ProjectPageLayout - redirecting to projects list. Missing account ID in URL');
      navigate(getProjectListUrl());
    }
  }, [variant, accountIdToUse]);
  useEffect(() => {
    if (redirectToProductIds && !toolIdFromProp) {
      warn("ProjectPageLayout: redirectToProductIds & currentToolId must be passed together. Got: redirectToProductIds: ".concat(redirectToProductIds, " currentToolId: ").concat(toolIdFromProp, ". Ignoring similar product redirects."));
    }
  }, [redirectToProductIds, toolIdFromProp]);
  return /*#__PURE__*/React.createElement(AlloyProvider, {
    projectId: projectIdToUse,
    accountId: accountIdToUse,
    productId: productIdToUse,
    toolId: toolIdToUse,
    locale: locale,
    autoFetchEntities: autoFetchEntities
  }, /*#__PURE__*/React.createElement(RefreshTokenSetter, null), !productIdToUse && isProductRequired && /*#__PURE__*/React.createElement(CurrentProductSetter, {
    setProductId: setProductFromIdState,
    currentToolId: toolIdToUse,
    redirectToProductIds: redirectToProductIds,
    variant: variant
  }), !toolIdFromProp && /*#__PURE__*/React.createElement(CurrentToolSetter, {
    setToolId: setToolFromIdState,
    setIsToolIdDetermined: setIsToolIdDetermined
  }), /*#__PURE__*/React.createElement(AnalyticsInitializer, {
    toolIdToUse: toolIdToUse,
    isToolIdDetermined: isToolIdDetermined,
    isProductRequired: isProductRequired,
    productIdToUse: productIdToUse
  }), /*#__PURE__*/React.createElement(CheckRedirects, {
    accountId: accountIdToUse,
    currentToolId: toolIdToUse,
    redirectToProductIds: redirectToProductIds,
    variant: variant
  }), /*#__PURE__*/React.createElement(CreateAndCheckCurrentProductLicense, {
    currentToolId: toolIdToUse,
    isProductRequired: isProductRequired,
    variant: variant
  }), /*#__PURE__*/React.createElement(ProjectPageLayoutView, _extends({
    ref: ref,
    currentToolId: toolIdToUse,
    showAccountsInHeader: !!accountIdToUse,
    variant: variant,
    isProductRequired: isProductRequired,
    onProductClick: onProductClick,
    onProjectClick: onProjectClick,
    onAccountClick: onAccountClick
  }, rest), children));
});
ProjectPageLayout.AUTO_FETCH_TYPES = AlloyProvider.AUTO_FETCH_TYPES;
ProjectPageLayout.PRODUCT_IDS = PRODUCT_IDS;
ProjectPageLayout.SERVICE_TYPES = SERVICE_TYPES;
ProjectPageLayout.TOOL_IDS = TOOL_IDS;
ProjectPageLayout.VARIANTS = PAGE_VARIANTS;
ProjectPageLayout.displayName = 'ProjectPageLayout';
ProjectPageLayout.defaultProps = {
  variant: PAGE_VARIANTS.PROJECT,
  isProductRequired: true
};
ProjectPageLayout.propTypes = {
  /** Page content */
  children: PropTypes.node,
  /** Optional. The current project ID. If missing - taken from URL */
  projectId: PropTypes.string,
  /** Optional. The current account ID. If missing - taken from URL */
  accountId: PropTypes.string,
  /** Optional. The current product ID, to mark as selected in ProductPicker. If missing - taken from URL */
  currentProductId: PropTypes.oneOf(Object.values(PRODUCT_IDS)),
  /** Optional. The current tool ID, to mark as selected in side navigation. If missing - taken from URL */
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  /** Optional. Other product IDs that the user should be redirected to if they don't have access to current product */
  redirectToProductIds: PropTypes.arrayOf(PropTypes.oneOf(Object.values(PRODUCT_IDS))),
  /** localization, default: en */
  locale: PropTypes.string,
  /** Auto fetch for specific types. Can be used to remove fetching some data if not needed (users, companies, etc.) */
  autoFetchEntities: PropTypes.arrayOf(PropTypes.oneOf(Object.values(AlloyProvider.AUTO_FETCH_TYPES))),
  /** Override default side navigation component */
  renderSideNavigation: PropTypes.func,
  /** Top navigation render function */
  renderTopNavigation: PropTypes.func,
  /** callback called on logout */
  onLogout: PropTypes.func,
  /**
   * Page variant. Account/Project etc.
   * Needed in cases where the page is in Account mode, or when page need to support both account & project mode.
   */
  variant: PropTypes.oneOf(Object.values(PAGE_VARIANTS)),
  /** Override default help links */
  helpLinks: ProjectPageLayoutView.propTypes.helpLinks,
  /** required product, by default true
   *  when false and no currentProductId provided
   *  placeholder will be displayed instead
   */
  isProductRequired: PropTypes.bool,
  /** Custom product click handler */
  onProductClick: PropTypes.func,
  /** Custom project click handler */
  onProjectClick: PropTypes.func,
  /** Custom account click handler */
  onAccountClick: PropTypes.func,
  /** Items that will render above the layout*/
  renderAboveLayout: PropTypes.func
};
export default ProjectPageLayout;
//# sourceMappingURL=ProjectPageLayout.js.map